import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  AccountActivityRow,
  AccountingDashboardAccommodationDailyRow,
  AccountingDashboardCashBalanceDailyRow,
  AccountingDashboardFilterParams,
  AccountingDashboardFinancialDailyRow,
  AccountingDashboardGuestOriginRow,
  AccountingDashboardRecentJournalRow,
  AccountingDashboardRevenueGrowthDailyRow,
  AccountingDashboardSnapshot,
  AccountingPeriodRecord,
  AccountingReportFilterParams,
  AccountRecord,
  GeneralLedgerRow,
  JournalEntryRecord,
  TrialBalanceRow,
  AccountingSummaryKpiData,
  AccountingFinancialsData,
} from '../types/accounting';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }

  return Number(value || 0);
};

const mapTrialBalanceRow = (
  value: Record<string, unknown>,
): TrialBalanceRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  account_id: String(value.account_id || ''),
  account_code: String(value.account_code || ''),
  account_name: String(value.account_name || ''),
  account_type: value.account_type as TrialBalanceRow['account_type'],
  account_subtype: (value.account_subtype as string | null) ?? null,
  normal_balance: value.normal_balance as TrialBalanceRow['normal_balance'],
  is_active: Boolean(value.is_active),
  total_debits: toNumber(value.total_debits),
  total_credits: toNumber(value.total_credits),
  closing_balance: toNumber(value.closing_balance),
  first_entry_date: (value.first_entry_date as string | null) ?? null,
  last_entry_date: (value.last_entry_date as string | null) ?? null,
  journal_entry_count: toNumber(value.journal_entry_count),
});

const mapGeneralLedgerRow = (
  value: Record<string, unknown>,
): GeneralLedgerRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  journal_entry_id: String(value.journal_entry_id || ''),
  journal_entry_line_id: String(value.journal_entry_line_id || ''),
  entry_date: String(value.entry_date || ''),
  source_module: value.source_module as GeneralLedgerRow['source_module'],
  reference_type: (value.reference_type as string | null) ?? null,
  reference_id: (value.reference_id as string | null) ?? null,
  memo: (value.memo as string | null) ?? null,
  status: value.status as GeneralLedgerRow['status'],
  reversal_of_journal_entry_id:
    (value.reversal_of_journal_entry_id as string | null) ?? null,
  reversed_by_journal_entry_id:
    (value.reversed_by_journal_entry_id as string | null) ?? null,
  reversed_at: (value.reversed_at as string | null) ?? null,
  account_id: String(value.account_id || ''),
  account_code: String(value.account_code || ''),
  account_name: String(value.account_name || ''),
  account_type: value.account_type as GeneralLedgerRow['account_type'],
  account_subtype: (value.account_subtype as string | null) ?? null,
  normal_balance: value.normal_balance as GeneralLedgerRow['normal_balance'],
  line_description: (value.line_description as string | null) ?? null,
  debit: toNumber(value.debit),
  credit: toNumber(value.credit),
  balance_delta: toNumber(value.balance_delta),
  running_balance: toNumber(value.running_balance),
  journal_created_at: (value.journal_created_at as string | null) ?? null,
  line_created_at: (value.line_created_at as string | null) ?? null,
  created_by: (value.created_by as string | null) ?? null,
  line_created_by: (value.line_created_by as string | null) ?? null,
});

const mapAccountActivityRow = (
  value: Record<string, unknown>,
): AccountActivityRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  account_id: String(value.account_id || ''),
  account_code: String(value.account_code || ''),
  account_name: String(value.account_name || ''),
  account_type: value.account_type as AccountActivityRow['account_type'],
  account_subtype: (value.account_subtype as string | null) ?? null,
  normal_balance: value.normal_balance as AccountActivityRow['normal_balance'],
  entry_date: String(value.entry_date || ''),
  source_module: value.source_module as AccountActivityRow['source_module'],
  reference_type: (value.reference_type as string | null) ?? null,
  journal_entry_count: toNumber(value.journal_entry_count),
  journal_line_count: toNumber(value.journal_line_count),
  total_debits: toNumber(value.total_debits),
  total_credits: toNumber(value.total_credits),
  net_activity: toNumber(value.net_activity),
});

const mapDashboardFinancialDailyRow = (
  value: Record<string, unknown>,
): AccountingDashboardFinancialDailyRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  metric_date: String(value.metric_date || ''),
  journal_count: toNumber(value.journal_count),
  line_count: toNumber(value.line_count),
  income_amount: toNumber(value.income_amount),
  expense_amount: toNumber(value.expense_amount),
  cogs_amount: toNumber(value.cogs_amount),
  net_income_amount: toNumber(value.net_income_amount),
  net_cash_movement: toNumber(value.net_cash_movement),
  cash_inflow_amount: toNumber(value.cash_inflow_amount),
  cash_outflow_amount: toNumber(value.cash_outflow_amount),
  pos_revenue_amount: toNumber(value.pos_revenue_amount),
  accommodation_revenue_amount: toNumber(value.accommodation_revenue_amount),
});

const mapDashboardCashBalanceDailyRow = (
  value: Record<string, unknown>,
): AccountingDashboardCashBalanceDailyRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  metric_date: String(value.metric_date || ''),
  opening_cash_balance: toNumber(value.opening_cash_balance),
  daily_net_cash_movement: toNumber(value.daily_net_cash_movement),
  closing_cash_balance: toNumber(value.closing_cash_balance),
});

const mapDashboardRevenueGrowthDailyRow = (
  value: Record<string, unknown>,
): AccountingDashboardRevenueGrowthDailyRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  metric_date: String(value.metric_date || ''),
  pos_collected_amount: toNumber(value.pos_collected_amount),
  accommodation_revenue_amount: toNumber(value.accommodation_revenue_amount),
  total_revenue_amount: toNumber(value.total_revenue_amount),
  settled_orders_count: toNumber(value.settled_orders_count),
  settlement_count: toNumber(value.settlement_count),
  active_booking_count: toNumber(value.active_booking_count),
});

const mapDashboardAccommodationDailyRow = (
  value: Record<string, unknown>,
): AccountingDashboardAccommodationDailyRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  metric_date: String(value.metric_date || ''),
  property_id: String(value.property_id || ''),
  property_name: String(value.property_name || ''),
  currency: String(value.currency || ''),
  total_rooms_available: toNumber(value.total_rooms_available),
  rooms_occupied: toNumber(value.rooms_occupied),
  total_room_revenue: toNumber(value.total_room_revenue),
  occupancy_pct: toNumber(value.occupancy_pct),
  revpar: toNumber(value.revpar),
});

const mapDashboardRecentJournalRow = (
  value: Record<string, unknown>,
): AccountingDashboardRecentJournalRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  journal_entry_id: String(value.journal_entry_id || ''),
  created_at: String(value.created_at || ''),
  entry_date: String(value.entry_date || ''),
  source_module:
    value.source_module as AccountingDashboardRecentJournalRow['source_module'],
  reference_type: (value.reference_type as string | null) ?? null,
  reference_id: (value.reference_id as string | null) ?? null,
  memo: (value.memo as string | null) ?? null,
  display_memo: (value.display_memo as string | null) ?? null,
  related_bill_number: (value.related_bill_number as string | null) ?? null,
  total_debit: toNumber(value.total_debit),
  total_credit: toNumber(value.total_credit),
  amount: toNumber(value.amount),
});

const mapDashboardGuestOriginRow = (
  value: Record<string, unknown>,
): AccountingDashboardGuestOriginRow => ({
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  metric_date: String(value.metric_date || ''),
  country_id:
    value.country_id === null || value.country_id === undefined
      ? null
      : String(value.country_id),
  country_name: (value.country_name as string | null) ?? null,
  country_iso_code: (value.country_iso_code as string | null) ?? null,
  bookings_count: toNumber(value.bookings_count),
  total_revenue: toNumber(value.total_revenue),
  total_paid: toNumber(value.total_paid),
});

interface ReportQueryable {
  eq: (column: string, value: unknown) => ReportQueryable;
  gte: (column: string, value: unknown) => ReportQueryable;
  lte: (column: string, value: unknown) => ReportQueryable;
  or: (filter: string) => ReportQueryable;
}

const applyReportFilters = <T>(
  query: T,
  filters: AccountingReportFilterParams,
) => {
  let nextQuery = query as unknown as ReportQueryable;

  if (filters.outletId) {
    nextQuery = nextQuery.eq('outlet_id', filters.outletId);
  }

  if (filters.accountId) {
    nextQuery = nextQuery.eq('account_id', filters.accountId);
  }

  if (filters.sourceModule && filters.sourceModule !== 'all') {
    nextQuery = nextQuery.eq('source_module', filters.sourceModule);
  }

  if (filters.dateFrom) {
    nextQuery = nextQuery.gte('entry_date', filters.dateFrom);
  }

  if (filters.dateTo) {
    nextQuery = nextQuery.lte('entry_date', filters.dateTo);
  }

  if (filters.search?.trim()) {
    const trimmedSearch = filters.search.trim();
    nextQuery = nextQuery.or(
      [
        `account_code.ilike.%${trimmedSearch}%`,
        `account_name.ilike.%${trimmedSearch}%`,
        `reference_type.ilike.%${trimmedSearch}%`,
        `reference_id.ilike.%${trimmedSearch}%`,
        `memo.ilike.%${trimmedSearch}%`,
      ].join(','),
    );
  }

  return nextQuery as T;
};

const applyDashboardFilters = <
  T extends {
    eq: (column: string, value: unknown) => T;
    gte: (column: string, value: unknown) => T;
    lte: (column: string, value: unknown) => T;
  },
>(
  query: T,
  filters: AccountingDashboardFilterParams,
  dateColumn = 'metric_date',
) => {
  let nextQuery = query;

  if (filters.outletId) {
    nextQuery = nextQuery.eq('outlet_id', filters.outletId);
  }

  if (filters.dateFrom) {
    nextQuery = nextQuery.gte(dateColumn, filters.dateFrom);
  }

  if (filters.dateTo) {
    nextQuery = nextQuery.lte(dateColumn, filters.dateTo);
  }

  return nextQuery;
};

export const fetchTrialBalance = async (
  companyId: number,
  outletId?: string | null,
  search?: string,
): Promise<ApiResponse<TrialBalanceRow[]>> => {
  try {
    let query = supabase
      .from('trial_balance_view' as never)
      .select('*')
      .eq('company_id', companyId)
      .order('account_code', { ascending: true });

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    if (search?.trim()) {
      const trimmedSearch = search.trim();
      query = query.or(
        `account_code.ilike.%${trimmedSearch}%,account_name.ilike.%${trimmedSearch}%,account_subtype.ilike.%${trimmedSearch}%`,
      );
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) =>
        mapTrialBalanceRow(item as Record<string, unknown>),
      ),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch trial balance',
      data: [],
    };
  }
};

export const fetchGeneralLedger = async ({
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  ...filters
}: AccountingReportFilterParams): Promise<
  ApiResponse<PaginatedResponse<GeneralLedgerRow>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('general_ledger_view' as never)
      .select('*', { count: 'exact' })
      .eq('company_id', filters.companyId)
      .order('entry_date', { ascending: false })
      .order('journal_created_at', { ascending: false })
      .range(from, to);

    query = applyReportFilters(query, { page, pageSize, ...filters });

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data || []).map((item) =>
          mapGeneralLedgerRow(item as Record<string, unknown>),
        ),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch general ledger',
      data: undefined,
    };
  }
};

export const fetchAccountActivity = async ({
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  ...filters
}: AccountingReportFilterParams): Promise<
  ApiResponse<PaginatedResponse<AccountActivityRow>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('account_activity_view' as never)
      .select('*', { count: 'exact' })
      .eq('company_id', filters.companyId)
      .order('entry_date', { ascending: false })
      .order('account_code', { ascending: true })
      .range(from, to);

    query = applyReportFilters(query, { page, pageSize, ...filters });

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data || []).map((item) =>
          mapAccountActivityRow(item as Record<string, unknown>),
        ),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch account activity',
      data: undefined,
    };
  }
};

// ─── Financial Reports ────────────────────────────────────────────────────────

export interface PnlRow {
  section: 'Revenue' | 'COGS' | 'Gross Profit' | 'Expenses' | 'Net Income';
  account_code: string;
  account_name: string;
  account_type: string;
  amount: number;
  isSubtotal?: boolean;
}

export const fetchProfitAndLoss = async (
  companyId: number,
  outletId: string | null,
  dateFrom: string | null,
  dateTo: string | null,
): Promise<PnlRow[]> => {
  let query = supabase
    .from('account_activity_view' as never)
    .select('*')
    .eq('company_id', companyId)
    .in('account_type', [
      'revenue',
      'cogs',
      'expense',
      'other_income',
      'other_expense',
    ]);

  if (outletId) query = query.eq('outlet_id', outletId);
  if (dateFrom) query = query.gte('entry_date', dateFrom);
  if (dateTo) query = query.lte('entry_date', dateTo);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  const rows = (data || []) as Array<Record<string, unknown>>;

  // Aggregate by account across all dates in range
  const byAccount = new Map<
    string,
    {
      code: string;
      name: string;
      type: string;
      credits: number;
      debits: number;
    }
  >();
  rows.forEach((r) => {
    const key = String(r.account_id || '');
    const existing = byAccount.get(key) ?? {
      code: String(r.account_code || ''),
      name: String(r.account_name || ''),
      type: String(r.account_type || ''),
      credits: 0,
      debits: 0,
    };
    existing.credits += toNumber(r.total_credits);
    existing.debits += toNumber(r.total_debits);
    byAccount.set(key, existing);
  });

  const result: PnlRow[] = [];
  let totalRevenue = 0;
  let totalCogs = 0;
  let totalExpenses = 0;

  const sectionOrder: Array<[string, 'Revenue' | 'COGS' | 'Expenses']> = [
    ['revenue', 'Revenue'],
    ['other_income', 'Revenue'],
    ['cogs', 'COGS'],
    ['expense', 'Expenses'],
    ['other_expense', 'Expenses'],
  ];

  sectionOrder.forEach(([type, section]) => {
    Array.from(byAccount.values()).forEach((acct) => {
      if (acct.type !== type) return;
      // Revenue: credit-normal → net = credits - debits
      // COGS/Expense: debit-normal → net = debits - credits
      const amount =
        section === 'Revenue'
          ? acct.credits - acct.debits
          : acct.debits - acct.credits;

      if (section === 'Revenue') totalRevenue += amount;
      else if (section === 'COGS') totalCogs += amount;
      else totalExpenses += amount;

      result.push({
        section,
        account_code: acct.code,
        account_name: acct.name,
        account_type: acct.type,
        amount,
      });
    });
  });

  const grossProfit = totalRevenue - totalCogs;
  const netIncome = grossProfit - totalExpenses;

  result.push({
    section: 'Gross Profit',
    account_code: '',
    account_name: 'Gross Profit',
    account_type: '',
    amount: grossProfit,
    isSubtotal: true,
  });
  result.push({
    section: 'Net Income',
    account_code: '',
    account_name: 'Net Income',
    account_type: '',
    amount: netIncome,
    isSubtotal: true,
  });

  return result;
};

export interface TaxReportRow {
  entry_date: string;
  source_module: string;
  reference_type: string | null;
  debit: number;
  credit: number;
  running_balance: number;
}

export const fetchTaxReport = async (
  companyId: number,
  outletId: string | null,
  dateFrom: string | null,
  dateTo: string | null,
): Promise<TaxReportRow[]> => {
  let query = supabase
    .from('general_ledger_view' as never)
    .select('*')
    .eq('company_id', companyId)
    .eq('account_code', '2040')
    .eq('status', 'posted')
    .order('entry_date', { ascending: true });

  if (outletId) query = query.eq('outlet_id', outletId);
  if (dateFrom) query = query.gte('entry_date', dateFrom);
  if (dateTo) query = query.lte('entry_date', dateTo);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  return ((data || []) as Array<Record<string, unknown>>).map((r) => ({
    entry_date: String(r.entry_date || ''),
    source_module: String(r.source_module || ''),
    reference_type: (r.reference_type as string | null) ?? null,
    debit: toNumber(r.debit),
    credit: toNumber(r.credit),
    running_balance: toNumber(r.running_balance),
  }));
};

export interface CogsReportRow {
  account_code: string;
  account_name: string;
  entry_date: string;
  source_module: string;
  debit: number;
  credit: number;
}

export const fetchCogsReport = async (
  companyId: number,
  outletId: string | null,
  dateFrom: string | null,
  dateTo: string | null,
): Promise<CogsReportRow[]> => {
  let query = supabase
    .from('general_ledger_view' as never)
    .select('*')
    .eq('company_id', companyId)
    .eq('account_type', 'cogs')
    .eq('status', 'posted')
    .order('entry_date', { ascending: false });

  if (outletId) query = query.eq('outlet_id', outletId);
  if (dateFrom) query = query.gte('entry_date', dateFrom);
  if (dateTo) query = query.lte('entry_date', dateTo);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  return ((data || []) as Array<Record<string, unknown>>).map((r) => ({
    account_code: String(r.account_code || ''),
    account_name: String(r.account_name || ''),
    entry_date: String(r.entry_date || ''),
    source_module: String(r.source_module || ''),
    debit: toNumber(r.debit),
    credit: toNumber(r.credit),
  }));
};

export interface DeferredRevenueRow {
  account_code: string;
  account_name: string;
  account_subtype: string | null;
  total_debits: number;
  total_credits: number;
  closing_balance: number;
}

export const fetchDeferredRevenue = async (
  companyId: number,
  outletId: string | null,
): Promise<DeferredRevenueRow[]> => {
  let query = supabase
    .from('trial_balance_view' as never)
    .select('*')
    .eq('company_id', companyId)
    .in('account_code', ['2100', '2120', '1110']);

  if (outletId) query = query.eq('outlet_id', outletId);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  return ((data || []) as Array<Record<string, unknown>>).map((r) => ({
    account_code: String(r.account_code || ''),
    account_name: String(r.account_name || ''),
    account_subtype: (r.account_subtype as string | null) ?? null,
    total_debits: toNumber(r.total_debits),
    total_credits: toNumber(r.total_credits),
    closing_balance: toNumber(r.closing_balance),
  }));
};

export interface ExpensesReportRow {
  account_code: string;
  account_name: string;
  account_subtype: string | null;
  total_debits: number;
  total_credits: number;
  net_amount: number;
}

export const fetchExpensesReport = async (
  companyId: number,
  outletId: string | null,
  dateFrom: string,
  dateTo: string,
): Promise<ExpensesReportRow[]> => {
  let query = supabase
    .from('general_ledger_view' as never)
    .select('account_code, account_name, account_subtype, debit, credit')
    .eq('company_id', companyId)
    .eq('status', 'posted')
    .in('account_type', ['expense', 'other_expense'])
    .gte('entry_date', dateFrom)
    .lte('entry_date', dateTo);

  if (outletId) query = query.eq('outlet_id', outletId);

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  const rows = (data || []) as Array<Record<string, unknown>>;
  const grouped = rows.reduce<Map<string, ExpensesReportRow>>((map, r) => {
    const code = String(r.account_code || '');
    const existing = map.get(code);
    if (existing) {
      existing.total_debits += toNumber(r.debit);
      existing.total_credits += toNumber(r.credit);
      existing.net_amount = existing.total_debits - existing.total_credits;
    } else {
      const debits = toNumber(r.debit);
      const credits = toNumber(r.credit);
      map.set(code, {
        account_code: code,
        account_name: String(r.account_name || ''),
        account_subtype: (r.account_subtype as string | null) ?? null,
        total_debits: debits,
        total_credits: credits,
        net_amount: debits - credits,
      });
    }
    return map;
  }, new Map());

  return Array.from(grouped.values()).sort((a, b) =>
    a.account_code.localeCompare(b.account_code),
  );
};

export const fetchAccountingDashboardSnapshot = async (
  companyId: number,
  outletId?: string | null,
  dateFrom?: string | null,
  dateTo?: string | null,
): Promise<ApiResponse<AccountingDashboardSnapshot>> => {
  try {
    const dashboardFilters: AccountingDashboardFilterParams = {
      companyId,
      outletId,
      dateFrom,
      dateTo,
    };
    const filterByOutlet = <
      T extends { eq: (column: string, value: unknown) => T },
    >(
      query: T,
    ) => (outletId ? query.eq('outlet_id', outletId) : query);

    const [
      accountsResult,
      periodsResult,
      journalsResult,
      paymentMethodsResult,
      paymentMappingsResult,
      postingRulesResult,
      taxRatesResult,
      trialBalanceResult,
      financialDailyResult,
      cashBalanceDailyResult,
      revenueGrowthDailyResult,
      accommodationDailyResult,
      recentJournalsResult,
      guestOriginsResult,
      financialEventsResult,
      bookingComPayoutReconciliationResult,
      recentSettlementMethodsResult,
    ] = await Promise.all([
      supabase
        .from('accounts')
        .select('id, is_active')
        .eq('company_id', companyId),
      supabase
        .from('accounting_periods')
        .select('id, status')
        .eq('company_id', companyId),
      supabase
        .from('journal_entries')
        .select('id, status')
        .eq('company_id', companyId),
      supabase.from('payment_methods').select('id, type'),
      supabase
        .from('payment_method_account_mappings' as never)
        .select('id, payment_method_id')
        .eq('company_id', companyId),
      supabase
        .from('financial_posting_rules' as never)
        .select('id')
        .eq('company_id', companyId),
      supabase
        .from('tax_rates' as never)
        .select('id, status')
        .eq('company_id', companyId),
      filterByOutlet(
        supabase
          .from('trial_balance_view' as never)
          .select('account_id, closing_balance')
          .eq('company_id', companyId),
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('business_dashboard_financial_daily_view' as never)
            .select('*')
            .eq('company_id', companyId)
            .order('metric_date', { ascending: true }),
        ),
        dashboardFilters,
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('business_dashboard_cash_balance_daily_view' as never)
            .select('*')
            .eq('company_id', companyId)
            .order('metric_date', { ascending: true }),
        ),
        dashboardFilters,
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('business_dashboard_revenue_growth_daily_view' as never)
            .select('*')
            .eq('company_id', companyId)
            .order('metric_date', { ascending: true }),
        ),
        dashboardFilters,
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('business_dashboard_accommodation_daily_view' as never)
            .select('*')
            .eq('company_id', companyId)
            .order('metric_date', { ascending: true }),
        ),
        dashboardFilters,
      ),
      applyDashboardFilters(
        supabase
          .from('business_dashboard_recent_journals_view' as never)
          .select('*')
          .eq('company_id', companyId)
          .order('entry_date', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(8),
        dashboardFilters,
        'entry_date',
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('business_dashboard_guest_origins_view' as never)
            .select('*')
            .eq('company_id', companyId)
            .order('total_revenue', { ascending: false }),
        ),
        dashboardFilters,
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('financial_event_logs' as never)
            .select('id, status')
            .eq('company_id', companyId),
        ),
        dashboardFilters,
        'created_at',
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('booking_com_payout_reconciliation_view' as never)
            .select(
              'payout_id, linked_bookings_count, gross_difference, commission_difference, net_difference',
            )
            .eq('company_id', companyId),
        ),
        dashboardFilters,
        'received_at',
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('order_settlements_view' as never)
            .select('payment_method_id')
            .eq('company_id', companyId),
        ),
        dashboardFilters,
        'created_at',
      ),
    ]);

    const firstError = [
      accountsResult.error,
      periodsResult.error,
      journalsResult.error,
      paymentMappingsResult.error,
      postingRulesResult.error,
      taxRatesResult.error,
      paymentMethodsResult.error,
      trialBalanceResult.error,
      financialDailyResult.error,
      cashBalanceDailyResult.error,
      revenueGrowthDailyResult.error,
      accommodationDailyResult.error,
      recentJournalsResult.error,
      guestOriginsResult.error,
      financialEventsResult.error,
      bookingComPayoutReconciliationResult.error,
      recentSettlementMethodsResult.error,
    ].find(Boolean);

    if (firstError) {
      return {
        success: false,
        error: firstError?.message,
        data: undefined,
      };
    }

    const accounts = (accountsResult.data || []) as AccountRecord[];
    const periods = (periodsResult.data || []) as AccountingPeriodRecord[];
    const journals = (journalsResult.data || []) as JournalEntryRecord[];
    const taxRates = (taxRatesResult.data || []) as Array<{ status: string }>;
    const paymentMethods = (paymentMethodsResult.data || []) as Array<{
      id: number;
      type: string;
    }>;
    const paymentMethodIdsWithMappings = new Set(
      (
        (paymentMappingsResult.data || []) as Array<{
          payment_method_id?: number;
        }>
      )
        .map((item) => item.payment_method_id)
        .filter((value): value is number => typeof value === 'number'),
    );
    const trialBalanceRows = (trialBalanceResult.data || []) as Array<{
      closing_balance: unknown;
    }>;
    const financialDailyRows = (
      (financialDailyResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardFinancialDailyRow(item));
    const cashBalanceDailyRows = (
      (cashBalanceDailyResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardCashBalanceDailyRow(item));
    const revenueGrowthDailyRows = (
      (revenueGrowthDailyResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardRevenueGrowthDailyRow(item));
    const accommodationDailyRows = (
      (accommodationDailyResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardAccommodationDailyRow(item));
    const recentJournalRows = (
      (recentJournalsResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardRecentJournalRow(item));
    const guestOriginDailyRows = (
      (guestOriginsResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardGuestOriginRow(item));
    const financialEvents = (financialEventsResult.data || []) as Array<{
      id: string;
      status: string;
    }>;
    const bookingComPayoutReconciliationRows = (
      (bookingComPayoutReconciliationResult.data || []) as Array<
        Record<string, unknown>
      >
    ).map((item) => ({
      payout_id: String(item.payout_id || ''),
      linked_bookings_count: toNumber(item.linked_bookings_count),
      gross_difference: toNumber(item.gross_difference),
      commission_difference: toNumber(item.commission_difference),
      net_difference: toNumber(item.net_difference),
    }));
    const recentSettlementMethods = (
      (recentSettlementMethodsResult.data || []) as Array<
        Record<string, unknown>
      >
    ).map((item) => toNumber(item.payment_method_id));

    const guestOriginsByCountry = Array.from(
      guestOriginDailyRows
        .reduce<
          Map<
            string,
            {
              country_id: string | null;
              country_name: string | null;
              country_iso_code: string | null;
              bookings_count: number;
              total_revenue: number;
              total_paid: number;
            }
          >
        >((map, row) => {
          const key =
            row.country_id ||
            row.country_iso_code ||
            row.country_name ||
            'unknown';
          const current = map.get(key) || {
            country_id: row.country_id,
            country_name: row.country_name,
            country_iso_code: row.country_iso_code,
            bookings_count: 0,
            total_revenue: 0,
            total_paid: 0,
          };

          current.bookings_count += row.bookings_count;
          current.total_revenue += row.total_revenue;
          current.total_paid += row.total_paid;

          map.set(key, current);
          return map;
        }, new Map())
        .values(),
    )
      .map((row) => ({
        company_id: companyId,
        outlet_id: outletId ?? null,
        metric_date: dateTo || dateFrom || '',
        country_id: row.country_id,
        country_name: row.country_name,
        country_iso_code: row.country_iso_code,
        bookings_count: row.bookings_count,
        total_revenue: row.total_revenue,
        total_paid: row.total_paid,
      }))
      .sort((left, right) => right.total_revenue - left.total_revenue)
      .slice(0, 6);

    const totalIncome = financialDailyRows.reduce(
      (sum, row) => sum + row.income_amount,
      0,
    );
    const totalExpenses = financialDailyRows.reduce(
      (sum, row) => sum + row.expense_amount,
      0,
    );
    const totalCogs = financialDailyRows.reduce(
      (sum, row) => sum + row.cogs_amount,
      0,
    );
    const netIncome = financialDailyRows.reduce(
      (sum, row) => sum + row.net_income_amount,
      0,
    );
    const netCashMovement = financialDailyRows.reduce(
      (sum, row) => sum + row.net_cash_movement,
      0,
    );
    const cashInflow = financialDailyRows.reduce(
      (sum, row) => sum + row.cash_inflow_amount,
      0,
    );
    const cashOutflow = financialDailyRows.reduce(
      (sum, row) => sum + row.cash_outflow_amount,
      0,
    );
    const totalTransactions = financialDailyRows.reduce(
      (sum, row) => sum + row.journal_count,
      0,
    );
    const totalJournalLines = financialDailyRows.reduce(
      (sum, row) => sum + row.line_count,
      0,
    );
    const totalSettledOrders = revenueGrowthDailyRows.reduce(
      (sum, row) => sum + row.settled_orders_count,
      0,
    );
    const totalSettlements = revenueGrowthDailyRows.reduce(
      (sum, row) => sum + row.settlement_count,
      0,
    );
    const totalActiveBookings = revenueGrowthDailyRows.reduce(
      (sum, row) => sum + row.active_booking_count,
      0,
    );
    const openingCashBalance =
      cashBalanceDailyRows[0]?.opening_cash_balance || 0;
    const closingCashBalance =
      cashBalanceDailyRows[cashBalanceDailyRows.length - 1]
        ?.closing_cash_balance || openingCashBalance;
    const totalAvailableRoomNights = accommodationDailyRows.reduce(
      (sum, row) => sum + row.total_rooms_available,
      0,
    );
    const totalOccupiedRoomNights = accommodationDailyRows.reduce(
      (sum, row) => sum + row.rooms_occupied,
      0,
    );
    const totalAccommodationRevenue = accommodationDailyRows.reduce(
      (sum, row) => sum + row.total_room_revenue,
      0,
    );
    const occupancyRate =
      totalAvailableRoomNights > 0
        ? Number(
            (
              (totalOccupiedRoomNights / totalAvailableRoomNights) *
              100
            ).toFixed(1),
          )
        : 0;
    const averageDailyRate =
      totalOccupiedRoomNights > 0
        ? totalAccommodationRevenue / totalOccupiedRoomNights
        : 0;
    const averageRevPar =
      totalAvailableRoomNights > 0
        ? totalAccommodationRevenue / totalAvailableRoomNights
        : 0;
    const averageTicketSize =
      totalSettledOrders > 0 ? totalIncome / totalSettledOrders : 0;
    const averageBookingValue =
      totalActiveBookings > 0
        ? totalAccommodationRevenue / totalActiveBookings
        : 0;
    const today = new Date().toISOString().slice(0, 10);
    const upcomingCheckoutWindowEnd = new Date();
    upcomingCheckoutWindowEnd.setDate(upcomingCheckoutWindowEnd.getDate() + 2);
    const upcomingCheckoutTo = upcomingCheckoutWindowEnd
      .toISOString()
      .slice(0, 10);

    const [upcomingCheckoutsResult, operationalBookingsResult] =
      await Promise.all([
        supabase
          .from('accommodation_bookings')
          .select('id')
          .gte('check_out', today)
          .lte('check_out', upcomingCheckoutTo)
          .not('status', 'in', '("cancelled","checked_out","no_show")'),
        supabase
          .from('accommodation_bookings')
          .select('id, total_amount, amount_paid, check_out, status')
          .not('status', 'in', '("cancelled","no_show")'),
      ]);

    const operationalError = [
      upcomingCheckoutsResult.error,
      operationalBookingsResult.error,
    ].find(Boolean);

    if (operationalError) {
      return {
        success: false,
        error: operationalError?.message,
        data: undefined,
      };
    }

    const mappablePaymentMethods = paymentMethods.filter(
      (method) => !['status'].includes(method.type),
    );
    const configuredMappings = paymentMappingsResult.data?.length || 0;
    const unmappedPaymentMethods = Math.max(
      0,
      mappablePaymentMethods.length - configuredMappings,
    );
    const operationalBookings = (operationalBookingsResult.data ||
      []) as Array<{
      id: string;
      total_amount: number;
      amount_paid: number;
      check_out: string;
      status: string;
    }>;
    const unpaidBookings = operationalBookings.filter(
      (booking) =>
        Number(booking.total_amount || 0) > Number(booking.amount_paid || 0),
    ).length;
    const overdueReceivables = operationalBookings.filter(
      (booking) =>
        booking.status === 'checked_out' &&
        booking.check_out < today &&
        Number(booking.total_amount || 0) > Number(booking.amount_paid || 0),
    ).length;
    const recentlyUsedUnmappedPaymentMethods = new Set(
      recentSettlementMethods.filter((paymentMethodId) => {
        const paymentMethod = paymentMethods.find(
          (item) => item.id === paymentMethodId,
        );

        if (!paymentMethod || paymentMethod.type === 'status') {
          return false;
        }

        return !paymentMethodIdsWithMappings.has(paymentMethodId);
      }),
    ).size;
    const failedFinancialEvents = financialEvents.filter(
      (event) => event.status === 'failed',
    ).length;
    const pendingFinancialEvents = financialEvents.filter(
      (event) => event.status === 'pending',
    ).length;
    const unmatchedBookingComPayouts =
      bookingComPayoutReconciliationRows.filter(
        (row) =>
          row.linked_bookings_count === 0 ||
          Math.abs(row.gross_difference) > 0.009 ||
          Math.abs(row.commission_difference) > 0.009 ||
          Math.abs(row.net_difference) > 0.009,
      ).length;

    return {
      success: true,
      data: {
        totalAccounts: accounts.length,
        activeAccounts: accounts.filter((account) => account.is_active).length,
        openPeriods: periods.filter((period) => period.status === 'open')
          .length,
        closedPeriods: periods.filter((period) => period.status === 'closed')
          .length,
        draftJournals: journals.filter((journal) => journal.status === 'draft')
          .length,
        postedJournals: journals.filter(
          (journal) => journal.status === 'posted',
        ).length,
        reversedJournals: journals.filter(
          (journal) => journal.status === 'reversed',
        ).length,
        configuredPaymentMappings: configuredMappings,
        configuredPostingRules: postingRulesResult.data?.length || 0,
        activeTaxRates: taxRates.filter((rate) => rate.status === 'active')
          .length,
        trialBalanceAccounts: trialBalanceRows.length,
        trialBalanceNetBalance: trialBalanceRows.reduce(
          (sum, row) => sum + toNumber(row.closing_balance),
          0,
        ),
        totalIncome,
        totalExpenses,
        totalCogs,
        netIncome,
        netCashMovement,
        cashPosition: closingCashBalance,
        cashInflow,
        cashOutflow,
        openingCashBalance,
        closingCashBalance,
        occupancyRate,
        averageDailyRate,
        averageRevPar,
        averageTicketSize,
        averageBookingValue,
        upcomingCheckouts: upcomingCheckoutsResult.data?.length || 0,
        unpaidBookings,
        overdueReceivables,
        unmappedPaymentMethods,
        recentlyUsedUnmappedPaymentMethods,
        failedFinancialEvents,
        pendingFinancialEvents,
        unmatchedBookingComPayouts,
        totalTransactions,
        totalJournalLines,
        totalSettledOrders,
        totalSettlements,
        totalActiveBookings,
        financialDaily: financialDailyRows,
        cashBalanceDaily: cashBalanceDailyRows,
        revenueGrowthDaily: revenueGrowthDailyRows,
        accommodationDaily: accommodationDailyRows,
        recentJournals: recentJournalRows,
        guestOrigins: guestOriginsByCountry,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch accounting dashboard snapshot',
      data: undefined,
    };
  }
};

export const fetchAccountingSummaryKpis = async (
  companyId: number,
  outletId?: string | null,
  dateFrom?: string | null,
  dateTo?: string | null,
): Promise<ApiResponse<AccountingSummaryKpiData>> => {
  try {
    const dashboardFilters: AccountingDashboardFilterParams = {
      companyId,
      outletId,
      dateFrom,
      dateTo,
    };

    const filterByOutlet = <
      T extends { eq: (column: string, value: unknown) => T },
    >(
      query: T,
    ) => (outletId ? query.eq('outlet_id', outletId) : query);

    const [
      accountsResult,
      periodsResult,
      journalsResult,
      paymentMethodsResult,
      paymentMappingsResult,
      postingRulesResult,
      taxRatesResult,
      financialEventsResult,
      bookingComPayoutReconciliationResult,
      recentSettlementMethodsResult,
    ] = await Promise.all([
      supabase
        .from('accounts')
        .select('id, is_active')
        .eq('company_id', companyId),
      supabase
        .from('accounting_periods')
        .select('id, status')
        .eq('company_id', companyId),
      supabase
        .from('journal_entries')
        .select('id, status')
        .eq('company_id', companyId),
      supabase.from('payment_methods').select('id, type'),
      supabase
        .from('payment_method_account_mappings' as never)
        .select('id, payment_method_id')
        .eq('company_id', companyId),
      supabase
        .from('financial_posting_rules' as never)
        .select('id')
        .eq('company_id', companyId),
      supabase
        .from('tax_rates' as never)
        .select('id, status')
        .eq('company_id', companyId),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('financial_event_logs' as never)
            .select('id, status')
            .eq('company_id', companyId),
        ),
        dashboardFilters,
        'created_at',
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('booking_com_payout_reconciliation_view' as never)
            .select(
              'payout_id, linked_bookings_count, gross_difference, commission_difference, net_difference',
            )
            .eq('company_id', companyId),
        ),
        dashboardFilters,
        'received_at',
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('order_settlements_view' as never)
            .select('payment_method_id')
            .eq('company_id', companyId),
        ),
        dashboardFilters,
        'created_at',
      ),
    ]);

    const firstError = [
      accountsResult.error,
      periodsResult.error,
      journalsResult.error,
      paymentMappingsResult.error,
      postingRulesResult.error,
      taxRatesResult.error,
      paymentMethodsResult.error,
      financialEventsResult.error,
      bookingComPayoutReconciliationResult.error,
      recentSettlementMethodsResult.error,
    ].find(Boolean);

    if (firstError) {
      return { success: false, error: firstError?.message, data: undefined };
    }

    const today = new Date().toISOString().slice(0, 10);
    const upcomingCheckoutWindowEnd = new Date();
    upcomingCheckoutWindowEnd.setDate(upcomingCheckoutWindowEnd.getDate() + 2);
    const upcomingCheckoutTo = upcomingCheckoutWindowEnd
      .toISOString()
      .slice(0, 10);

    const [upcomingCheckoutsResult, operationalBookingsResult] =
      await Promise.all([
        supabase
          .from('accommodation_bookings')
          .select('id')
          .gte('check_out', today)
          .lte('check_out', upcomingCheckoutTo)
          .not('status', 'in', '("cancelled","checked_out","no_show")'),
        supabase
          .from('accommodation_bookings')
          .select('id, total_amount, amount_paid, check_out, status')
          .not('status', 'in', '("cancelled","no_show")'),
      ]);

    const operationalError = [
      upcomingCheckoutsResult.error,
      operationalBookingsResult.error,
    ].find(Boolean);

    if (operationalError) {
      return {
        success: false,
        error: operationalError?.message,
        data: undefined,
      };
    }

    const accounts = (accountsResult.data || []) as AccountRecord[];
    const periods = (periodsResult.data || []) as AccountingPeriodRecord[];
    const journals = (journalsResult.data || []) as JournalEntryRecord[];
    const taxRates = (taxRatesResult.data || []) as Array<{ status: string }>;
    const paymentMethods = (paymentMethodsResult.data || []) as Array<{
      id: number;
      type: string;
    }>;
    const paymentMethodIdsWithMappings = new Set(
      (
        (paymentMappingsResult.data || []) as Array<{
          payment_method_id?: number;
        }>
      )
        .map((item) => item.payment_method_id)
        .filter((value): value is number => typeof value === 'number'),
    );
    const financialEvents = (financialEventsResult.data || []) as Array<{
      id: string;
      status: string;
    }>;
    const bookingComPayoutReconciliationRows = (
      (bookingComPayoutReconciliationResult.data || []) as Array<
        Record<string, unknown>
      >
    ).map((item) => ({
      payout_id: String(item.payout_id || ''),
      linked_bookings_count: toNumber(item.linked_bookings_count),
      gross_difference: toNumber(item.gross_difference),
      commission_difference: toNumber(item.commission_difference),
      net_difference: toNumber(item.net_difference),
    }));
    const recentSettlementMethods = (
      (recentSettlementMethodsResult.data || []) as Array<
        Record<string, unknown>
      >
    ).map((item) => toNumber(item.payment_method_id));

    const mappablePaymentMethods = paymentMethods.filter(
      (method) => !['status'].includes(method.type),
    );
    const configuredMappings = paymentMappingsResult.data?.length || 0;
    const unmappedPaymentMethods = Math.max(
      0,
      mappablePaymentMethods.length - configuredMappings,
    );
    const operationalBookings = (operationalBookingsResult.data ||
      []) as Array<{
      id: string;
      total_amount: number;
      amount_paid: number;
      check_out: string;
      status: string;
    }>;
    const unpaidBookings = operationalBookings.filter(
      (booking) =>
        Number(booking.total_amount || 0) > Number(booking.amount_paid || 0),
    ).length;
    const overdueReceivables = operationalBookings.filter(
      (booking) =>
        booking.status === 'checked_out' &&
        booking.check_out < today &&
        Number(booking.total_amount || 0) > Number(booking.amount_paid || 0),
    ).length;
    const recentlyUsedUnmappedPaymentMethods = new Set(
      recentSettlementMethods.filter((paymentMethodId) => {
        const paymentMethod = paymentMethods.find(
          (item) => item.id === paymentMethodId,
        );

        if (!paymentMethod || paymentMethod.type === 'status') {
          return false;
        }

        return !paymentMethodIdsWithMappings.has(paymentMethodId);
      }),
    ).size;
    const failedFinancialEvents = financialEvents.filter(
      (event) => event.status === 'failed',
    ).length;
    const pendingFinancialEvents = financialEvents.filter(
      (event) => event.status === 'pending',
    ).length;
    const unmatchedBookingComPayouts =
      bookingComPayoutReconciliationRows.filter(
        (row) =>
          row.linked_bookings_count === 0 ||
          Math.abs(row.gross_difference) > 0.009 ||
          Math.abs(row.commission_difference) > 0.009 ||
          Math.abs(row.net_difference) > 0.009,
      ).length;

    return {
      success: true,
      data: {
        totalAccounts: accounts.length,
        activeAccounts: accounts.filter((account) => account.is_active).length,
        openPeriods: periods.filter((period) => period.status === 'open')
          .length,
        closedPeriods: periods.filter((period) => period.status === 'closed')
          .length,
        draftJournals: journals.filter((journal) => journal.status === 'draft')
          .length,
        postedJournals: journals.filter(
          (journal) => journal.status === 'posted',
        ).length,
        reversedJournals: journals.filter(
          (journal) => journal.status === 'reversed',
        ).length,
        configuredPaymentMappings: configuredMappings,
        configuredPostingRules: postingRulesResult.data?.length || 0,
        activeTaxRates: taxRates.filter((rate) => rate.status === 'active')
          .length,
        upcomingCheckouts: upcomingCheckoutsResult.data?.length || 0,
        unpaidBookings,
        overdueReceivables,
        unmappedPaymentMethods,
        recentlyUsedUnmappedPaymentMethods,
        failedFinancialEvents,
        pendingFinancialEvents,
        unmatchedBookingComPayouts,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch KPIs',
      data: undefined,
    };
  }
};

export const fetchAccountingFinancialsSnapshot = async (
  companyId: number,
  outletId?: string | null,
  dateFrom?: string | null,
  dateTo?: string | null,
): Promise<ApiResponse<AccountingFinancialsData>> => {
  try {
    const dashboardFilters: AccountingDashboardFilterParams = {
      companyId,
      outletId,
      dateFrom,
      dateTo,
    };
    const filterByOutlet = <
      T extends { eq: (column: string, value: unknown) => T },
    >(
      query: T,
    ) => (outletId ? query.eq('outlet_id', outletId) : query);

    const [
      trialBalanceResult,
      financialDailyResult,
      cashBalanceDailyResult,
      revenueGrowthDailyResult,
      accommodationDailyResult,
    ] = await Promise.all([
      filterByOutlet(
        supabase
          .from('trial_balance_view' as never)
          .select('account_id, closing_balance')
          .eq('company_id', companyId),
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('business_dashboard_financial_daily_view' as never)
            .select('*')
            .eq('company_id', companyId)
            .order('metric_date', { ascending: true }),
        ),
        dashboardFilters,
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('business_dashboard_cash_balance_daily_view' as never)
            .select('*')
            .eq('company_id', companyId)
            .order('metric_date', { ascending: true }),
        ),
        dashboardFilters,
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('business_dashboard_revenue_growth_daily_view' as never)
            .select('*')
            .eq('company_id', companyId)
            .order('metric_date', { ascending: true }),
        ),
        dashboardFilters,
      ),
      applyDashboardFilters(
        filterByOutlet(
          supabase
            .from('business_dashboard_accommodation_daily_view' as never)
            .select('*')
            .eq('company_id', companyId)
            .order('metric_date', { ascending: true }),
        ),
        dashboardFilters,
      ),
    ]);

    const firstError = [
      trialBalanceResult.error,
      financialDailyResult.error,
      cashBalanceDailyResult.error,
      revenueGrowthDailyResult.error,
      accommodationDailyResult.error,
    ].find(Boolean);

    if (firstError) {
      return { success: false, error: firstError?.message, data: undefined };
    }

    const trialBalanceRows = (trialBalanceResult.data || []) as Array<{
      closing_balance: unknown;
    }>;
    const financialDailyRows = (
      (financialDailyResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardFinancialDailyRow(item));
    const cashBalanceDailyRows = (
      (cashBalanceDailyResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardCashBalanceDailyRow(item));
    const revenueGrowthDailyRows = (
      (revenueGrowthDailyResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardRevenueGrowthDailyRow(item));
    const accommodationDailyRows = (
      (accommodationDailyResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardAccommodationDailyRow(item));

    const totalIncome = financialDailyRows.reduce(
      (sum, row) => sum + row.income_amount,
      0,
    );
    const totalExpenses = financialDailyRows.reduce(
      (sum, row) => sum + row.expense_amount,
      0,
    );
    const totalCogs = financialDailyRows.reduce(
      (sum, row) => sum + row.cogs_amount,
      0,
    );
    const netIncome = financialDailyRows.reduce(
      (sum, row) => sum + row.net_income_amount,
      0,
    );
    const netCashMovement = financialDailyRows.reduce(
      (sum, row) => sum + row.net_cash_movement,
      0,
    );
    const cashInflow = financialDailyRows.reduce(
      (sum, row) => sum + row.cash_inflow_amount,
      0,
    );
    const cashOutflow = financialDailyRows.reduce(
      (sum, row) => sum + row.cash_outflow_amount,
      0,
    );
    const totalTransactions = financialDailyRows.reduce(
      (sum, row) => sum + row.journal_count,
      0,
    );
    const totalJournalLines = financialDailyRows.reduce(
      (sum, row) => sum + row.line_count,
      0,
    );
    const totalSettledOrders = revenueGrowthDailyRows.reduce(
      (sum, row) => sum + row.settled_orders_count,
      0,
    );
    const totalSettlements = revenueGrowthDailyRows.reduce(
      (sum, row) => sum + row.settlement_count,
      0,
    );
    const totalActiveBookings = revenueGrowthDailyRows.reduce(
      (sum, row) => sum + row.active_booking_count,
      0,
    );
    const openingCashBalance =
      cashBalanceDailyRows[0]?.opening_cash_balance || 0;
    const closingCashBalance =
      cashBalanceDailyRows[cashBalanceDailyRows.length - 1]
        ?.closing_cash_balance || openingCashBalance;
    const totalAvailableRoomNights = accommodationDailyRows.reduce(
      (sum, row) => sum + row.total_rooms_available,
      0,
    );
    const totalOccupiedRoomNights = accommodationDailyRows.reduce(
      (sum, row) => sum + row.rooms_occupied,
      0,
    );
    const totalAccommodationRevenue = accommodationDailyRows.reduce(
      (sum, row) => sum + row.total_room_revenue,
      0,
    );
    const occupancyRate =
      totalAvailableRoomNights > 0
        ? Number(
            (
              (totalOccupiedRoomNights / totalAvailableRoomNights) *
              100
            ).toFixed(1),
          )
        : 0;
    const averageDailyRate =
      totalOccupiedRoomNights > 0
        ? totalAccommodationRevenue / totalOccupiedRoomNights
        : 0;
    const averageRevPar =
      totalAvailableRoomNights > 0
        ? totalAccommodationRevenue / totalAvailableRoomNights
        : 0;
    const averageTicketSize =
      totalSettledOrders > 0 ? totalIncome / totalSettledOrders : 0;
    const averageBookingValue =
      totalActiveBookings > 0
        ? totalAccommodationRevenue / totalActiveBookings
        : 0;

    return {
      success: true,
      data: {
        financialDaily: financialDailyRows,
        cashBalanceDaily: cashBalanceDailyRows,
        revenueGrowthDaily: revenueGrowthDailyRows,
        accommodationDaily: accommodationDailyRows,
        trialBalanceAccounts: trialBalanceRows.length,
        trialBalanceNetBalance: trialBalanceRows.reduce(
          (sum, row) => sum + toNumber(row.closing_balance),
          0,
        ),
        totalIncome,
        totalExpenses,
        totalCogs,
        netIncome,
        netCashMovement,
        cashPosition: closingCashBalance,
        cashInflow,
        cashOutflow,
        openingCashBalance,
        closingCashBalance,
        occupancyRate,
        averageDailyRate,
        averageRevPar,
        averageTicketSize,
        averageBookingValue,
        totalTransactions,
        totalJournalLines,
        totalSettledOrders,
        totalSettlements,
        totalActiveBookings,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch financials',
      data: undefined,
    };
  }
};

export const fetchAccountingRecentJournals = async (
  companyId: number,
  outletId?: string | null,
  dateFrom?: string | null,
  dateTo?: string | null,
): Promise<ApiResponse<AccountingDashboardRecentJournalRow[]>> => {
  try {
    const dashboardFilters: AccountingDashboardFilterParams = {
      companyId,
      outletId,
      dateFrom,
      dateTo,
    };

    const recentJournalsResult = await applyDashboardFilters(
      supabase
        .from('business_dashboard_recent_journals_view' as never)
        .select('*')
        .eq('company_id', companyId)
        .order('entry_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(8),
      dashboardFilters,
      'entry_date',
    );

    if (recentJournalsResult.error) {
      return {
        success: false,
        error: recentJournalsResult.error.message,
        data: undefined,
      };
    }

    const recentJournalRows = (
      (recentJournalsResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardRecentJournalRow(item));

    return {
      success: true,
      data: recentJournalRows,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch recent journals',
      data: undefined,
    };
  }
};

export const fetchAccountingGuestOrigins = async (
  companyId: number,
  outletId?: string | null,
  dateFrom?: string | null,
  dateTo?: string | null,
): Promise<ApiResponse<AccountingDashboardGuestOriginRow[]>> => {
  try {
    const dashboardFilters: AccountingDashboardFilterParams = {
      companyId,
      outletId,
      dateFrom,
      dateTo,
    };
    const filterByOutlet = <
      T extends { eq: (column: string, value: unknown) => T },
    >(
      query: T,
    ) => (outletId ? query.eq('outlet_id', outletId) : query);

    const guestOriginsResult = await applyDashboardFilters(
      filterByOutlet(
        supabase
          .from('business_dashboard_guest_origins_view' as never)
          .select('*')
          .eq('company_id', companyId)
          .order('total_revenue', { ascending: false }),
      ),
      dashboardFilters,
    );

    if (guestOriginsResult.error) {
      return {
        success: false,
        error: guestOriginsResult.error.message,
        data: undefined,
      };
    }

    const guestOriginDailyRows = (
      (guestOriginsResult.data || []) as Array<Record<string, unknown>>
    ).map((item) => mapDashboardGuestOriginRow(item));

    const guestOriginsByCountry = Array.from(
      guestOriginDailyRows
        .reduce<
          Map<
            string,
            {
              country_id: string | null;
              country_name: string | null;
              country_iso_code: string | null;
              bookings_count: number;
              total_revenue: number;
              total_paid: number;
            }
          >
        >((map, row) => {
          const key =
            row.country_id ||
            row.country_iso_code ||
            row.country_name ||
            'unknown';
          const current = map.get(key) || {
            country_id: row.country_id,
            country_name: row.country_name,
            country_iso_code: row.country_iso_code,
            bookings_count: 0,
            total_revenue: 0,
            total_paid: 0,
          };

          current.bookings_count += row.bookings_count;
          current.total_revenue += row.total_revenue;
          current.total_paid += row.total_paid;

          map.set(key, current);
          return map;
        }, new Map())
        .values(),
    )
      .map((row) => ({
        company_id: companyId,
        outlet_id: outletId ?? null,
        metric_date: dateTo || dateFrom || '',
        country_id: row.country_id,
        country_name: row.country_name,
        country_iso_code: row.country_iso_code,
        bookings_count: row.bookings_count,
        total_revenue: row.total_revenue,
        total_paid: row.total_paid,
      }))
      .sort((left, right) => right.total_revenue - left.total_revenue)
      .slice(0, 6);

    return {
      success: true,
      data: guestOriginsByCountry,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch guest origins',
      data: undefined,
    };
  }
};
